/**
 * `destacar` — rola até um elemento da tela aberta e o realça ([T015] / US-4).
 *
 * É a única das quatro tools de front SEM `modo` (assim está no backend): rolar
 * e piscar é inofensivo e reversível, então roda sempre direto.
 *
 * ★ A classe de realce é GLOBAL (`modules/aawz-intelligence-v2/design/_realce.sass`,
 *   importada por `src/design/index.sass`) porque o alvo está FORA deste módulo
 *   — é um card do Fee Fixo, uma coluna do BI, uma seção do CRM. Um
 *   `<style scoped>` nunca o alcançaria, e a falha seria silenciosa: a classe
 *   entra, nada muda de cor, e o teste que olha o `classList` continua verde.
 *   Mesmo motivo pelo qual `_fp-mobile` e `_az-sheet` já vivem no index global.
 */
export const NOME_DA_TOOL = 'destacar'
export const LABEL = 'Destacar na tela'

/** A classe que o `_realce.sass` estiliza. Espelho manual — mudou aqui, mude lá. */
export const CLASSE_DE_REALCE = 'iv2-realce'

/** Quanto tempo o realce fica. Curto o bastante para não virar poluição. */
export const DURACAO_DO_REALCE_MS = 3000

/**
 * Onde procurar o alvo, em ordem de intenção:
 * `data-destaque` é a marcação DEDICADA (quem quiser ser alcançável pela IA a
 * põe); `data-tour` é a marcação que o tour guiado do Fee Fixo já usa nos
 * mesmos elementos; `data-teste` é a de última instância.
 */
export const ATRIBUTOS_DE_ALVO = ['data-destaque', 'data-tour', 'data-teste']

const recusar = (motivo) => ({ ok: false, motivo: motivo })

/**
 * Acha o elemento. Devolve `null` — e NUNCA lança — quando o alvo tem caractere
 * que quebra o seletor: `querySelector('[data-tour="a"b"]')` lança
 * `SyntaxError`, e o turno morreria com um erro de CSS no lugar de um "não
 * achei" que o modelo sabe explicar.
 */
export function acharAlvo(alvo, id) {
  if (!alvo || typeof alvo !== 'string') return null
  if (typeof document === 'undefined') return null

  const seletores = []
  if (id) seletores.push('#' + id)
  ATRIBUTOS_DE_ALVO.forEach((atributo) => {
    seletores.push('[' + atributo + '="' + alvo + '"]')
  })
  seletores.push('#' + alvo)

  for (const seletor of seletores) {
    try {
      const el = document.querySelector(seletor)
      if (el) return el
    } catch (e) {
      // Seletor inválido: segue para o próximo em vez de derrubar o turno.
    }
  }
  return null
}

/**
 * @param {Object} input `{alvo, id?}`.
 * @returns {{ok: boolean, motivo?: string, elemento?: Element}}
 */
export function guard(input) {
  const dados = input || {}
  if (!dados.alvo) {
    return recusar('nenhum alvo informado para destacar.')
  }

  const elemento = acharAlvo(dados.alvo, dados.id)
  if (!elemento) {
    return recusar(
      'o alvo "' + dados.alvo + '" não está na tela aberta agora. Siga a ' +
        'resposta em texto, ou leve o usuário à tela certa antes.'
    )
  }

  return { ok: true, elemento: elemento }
}

/**
 * @returns {Promise<{destacado: boolean, motivo?: string}>}
 */
export function executar(input) {
  const veredito = guard(input)
  if (!veredito.ok) {
    return Promise.resolve({
      destacado: false,
      motivo: veredito.motivo,
      recusado: true,
    })
  }

  const elemento = veredito.elemento
  // `block: 'nearest'` de propósito: `center` sobe a cadeia de ancestrais
  // scrolláveis e reposiciona o container global — o mesmo efeito que já cortou
  // o header no chat de atendimento (`chat-window.vue:612`). `nearest` traz o
  // elemento à vista com o menor movimento possível.
  if (typeof elemento.scrollIntoView === 'function') {
    elemento.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }
  elemento.classList.add(CLASSE_DE_REALCE)
  setTimeout(() => {
    elemento.classList.remove(CLASSE_DE_REALCE)
  }, DURACAO_DO_REALCE_MS)

  return Promise.resolve({ destacado: true })
}

export default { nome: NOME_DA_TOOL, label: LABEL, guard: guard, executar: executar }
